import * as THREE from "three";
import { scene } from "./scene";
import { camera, controls } from "./camera";
import { renderer } from "./renderer";
import { PointerLockControls } from "three/examples/jsm/controls/PointerLockControls.js";
// import { clock } from "./clock"; // Removed shared clock to avoid delta race conditions
const localClock = new THREE.Clock();
import { joystickState } from "./joystick";

let cameraPosition = camera.position;
let cameraRotation = camera.rotation;
let fpsControls;
let fpsEnabled = false;
let isLocking = false;
let velocity = new THREE.Vector3();
let direction = new THREE.Vector3();
const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
camera.rotation.order = 'YXZ'; // Prevent roll when rotating Y and X
const moveState = {
  forward: false,
  back: false,
  left: false,
  right: false,
};
const keyboardEnabled = false; // keep keyboard idle unless FPS mode
const roomBounds = {
  minX: -5.8,
  maxX: 5.0,
  minZ: 3.1,
  maxZ: 9.3,
  minY: 1.0,
  maxY: 4.0,
};

function keysCamera() {
  document.addEventListener("keydown", function (event) {
    //console.log(event.keyCode);
    if (fpsEnabled) {
      if (event.code === "KeyW" || event.keyCode === 87) moveState.forward = true;
      if (event.code === "KeyS" || event.keyCode === 83) moveState.back = true;
      if (event.code === "KeyA" || event.keyCode === 65) moveState.left = true;
      if (event.code === "KeyD" || event.keyCode === 68) moveState.right = true;
      if (event.keyCode === 38) moveState.forward = true; // arrow up
      if (event.keyCode === 40) moveState.back = true; // arrow down
      if (event.keyCode === 37) moveState.left = true; // arrow left
      if (event.keyCode === 39) moveState.right = true; // arrow right
      return;
    }
    // outside FPS mode: keyboard is disabled to keep camera locked

    // HELPER: Premi 'P' per stampare la posizione corrente in console (utile per camera.js)
    if (event.code === "KeyP" || event.keyCode === 80) {
      console.log(`
    targetPosition = new THREE.Vector3(
      ${camera.position.x},
      ${camera.position.y},
      ${camera.position.z}
    );
    targetRotation = new THREE.Euler(
      ${camera.rotation.x},
      ${camera.rotation.y},
      ${camera.rotation.z},
      "${camera.rotation.order}"
    );
      `);
    }
  });

  document.addEventListener("keyup", function (event) {
    if (fpsEnabled) {
      if (event.code === "KeyW" || event.keyCode === 87) moveState.forward = false;
      if (event.code === "KeyS" || event.keyCode === 83) moveState.back = false;
      if (event.code === "KeyA" || event.keyCode === 65) moveState.left = false;
      if (event.code === "KeyD" || event.keyCode === 68) moveState.right = false;
      if (event.keyCode === 38) moveState.forward = false;
      if (event.keyCode === 40) moveState.back = false;
      if (event.keyCode === 37) moveState.left = false;
      if (event.keyCode === 39) moveState.right = false;
    }
  });
}

let i = 0;
let positionVal = "";
let rotationVal = "";
let temp = "";

function ensureFpsControls() {
  if (!fpsControls) {
    fpsControls = new PointerLockControls(camera, renderer.domElement);
    fpsControls.addEventListener("unlock", () => {
      fpsEnabled = false;
      if (controls) {
        // Relax constraints
        controls.minPolarAngle = 0;
        controls.maxPolarAngle = Math.PI;
        controls.minAzimuthAngle = -Infinity;
        controls.maxAzimuthAngle = Infinity;
        controls.minDistance = 0;
        controls.maxDistance = Infinity;

        // Force the target to be exactly horizontal in front of the camera
        // to prevent any "snapping to ground" issues caused by pitch accumulation
        const dir = new THREE.Vector3();
        camera.getWorldDirection(dir);
        dir.y = 0; // Force perfectly horizontal
        if (dir.lengthSq() < 0.001) {
            dir.set(0, 0, -1); // Fallback direction if looking straight down/up
        }
        dir.normalize();
        
        controls.target.copy(camera.position).add(dir.multiplyScalar(2.0));

        controls.enabled = false;
        controls.update(); 
      }
    });
  }
  return fpsControls;
}

function enableFpsMode() {
  if (isLocking) return;
  const controls = ensureFpsControls();
  
  isLocking = true;
  fpsEnabled = true; // Set flag early, before lock() which might fail on mobile
  
  try {
    if (!isMobile) {
      controls.lock();
    }
    // allow small upward offset to feel like human height
    if (camera.position.y < 1.4) {
      camera.position.y = 1.6;
    }
  } catch (err) {
    console.warn("PointerLock rejected:", err);
  } finally {
    // Release the guard after a short delay to allow the browser to settle
    setTimeout(() => { isLocking = false; }, 100);
  }
}

function disableFpsMode() {
  if (!fpsControls || !fpsEnabled) return;
  // Only call unlock if the browser actually has the lock
  if (document.pointerLockElement) {
    fpsControls.unlock();
  }
  fpsEnabled = false;
}

function isFpsMode() {
  return fpsEnabled;
}

function updateFps(delta) {
  if (!fpsControls) ensureFpsControls();
  if (!fpsEnabled && !isMobile) return;

  velocity.x -= velocity.x * 8.0 * delta;
  velocity.z -= velocity.z * 8.0 * delta;

  direction.z = Number(moveState.forward) - Number(moveState.back);
  direction.x = Number(moveState.right) - Number(moveState.left);
  direction.normalize();

  const speed = 16.8;
  if (moveState.forward || moveState.back) velocity.z += direction.z * speed * delta;
  if (moveState.left || moveState.right) velocity.x += direction.x * speed * delta;

  fpsControls.moveRight(velocity.x * delta);
  fpsControls.moveForward(velocity.z * delta);

  // Mobile Joytick Integration
  const joystickSpeed = 5.0;
  if (joystickState.move.x !== 0 || joystickState.move.y !== 0) {
    fpsControls.moveRight(joystickState.move.x * joystickSpeed * delta);
    fpsControls.moveForward(joystickState.move.y * joystickSpeed * delta);
  }

  const lookSpeed = 2.0;
  if (joystickState.look.x !== 0 || joystickState.look.y !== 0) {
    camera.rotation.y -= joystickState.look.x * lookSpeed * delta;
    // Note: Pitch (look up/down) is trickier with PointerLockControls, 
    // but let's start with yaw for now as requested for "videogame" feel
  }

  const pos = fpsControls.getObject().position;
  pos.x = THREE.MathUtils.clamp(pos.x, roomBounds.minX, roomBounds.maxX);
  pos.z = THREE.MathUtils.clamp(pos.z, roomBounds.minZ, roomBounds.maxZ);
  pos.y = THREE.MathUtils.clamp(pos.y, roomBounds.minY, roomBounds.maxY);
  camera.position.copy(pos);
}

function renderCamera() {
  const delta = localClock.getDelta();
  if (fpsEnabled || isMobile) {
    updateFps(delta);
  } 
  
  if (!fpsEnabled && controls && controls.enabled) {
    controls.update();
  }
  i++;
  requestAnimationFrame(renderCamera);
  renderer.render(scene, camera);
  if (i > 300) {
    //console.log(camera.position);
    //console.log(camera.rotation);

    const positionValInput = document.getElementById("positionVal");
    const rotationValInput = document.getElementById("rotationVal");
    if (positionValInput && rotationValInput) {
      positionValInput.value =
        camera.position.x + "," + camera.position.y + "," + camera.position.z;
      rotationValInput.value =
        camera.rotation.x + "," + camera.rotation.y + "," + camera.rotation.z;
    }

    i = 0;
  }
}

renderCamera();

export { keysCamera, enableFpsMode, disableFpsMode, isFpsMode };
